import type { CoachingFormuleId } from '../config/coaching';

/**
 * TARIFS DE COACHING — LUS AU SERVEUR, JAMAIS ÉCRITS ICI.
 *
 * `src/config/coaching.ts` portait « 50 € » et « 99 € » en dur, recopiés à la main depuis
 * `COACHING_AMOUNTS_EUR` côté serveur. Deux vérités pour la même somme, et un commentaire qui
 * demandait de les synchroniser — c'est-à-dire qui admettait le défaut. La route de lecture de
 * `coaching-payment` rend désormais l'original ; ce module le représente.
 */

export interface Tarif {
  /** En CENTIMES, comme le serveur. Aucune conversion en route : c'est l'unité de comparaison. */
  amount: number;
  currency: string;
}

/**
 * TROIS ÉTATS, ET UN TYPE SOMME PLUTÔT QUE DES CHAMPS NULLABLES.
 *
 * Un objet `{ tarifs: Tarif | null }` laisserait écrire `tarifs?.amount ?? 5000` — la ligne exacte
 * qu'on interdit. Un type somme rend ce repli impossible à exprimer : il n'y a rien à quoi
 * l'accrocher tant que le statut n'est pas `charge`.
 */
export type EtatTarifs =
  | { statut: 'chargement' }
  | { statut: 'charge'; tarifs: Partial<Record<CoachingFormuleId, Tarif>> }
  | { statut: 'indisponible' };

/**
 * UN CATALOGUE INCOMPLET N'EST PAS UNE ERREUR GLOBALE. Si le serveur renvoie une formule et pas
 * l'autre — renommage en cours, déploiement partiel —, seule la formule manquante devient
 * indisponible. `charge` avec un `Partial` dit exactement ça, et `tarifDe` rend `null` pour celle
 * qui manque : aucun écran n'a à distinguer « pas encore chargé » de « absent du catalogue », il
 * suffit de ne rien afficher et de désactiver.
 */
export function tarifDe(etat: EtatTarifs, formule: CoachingFormuleId): Tarif | null {
  return etat.statut === 'charge' ? etat.tarifs[formule] ?? null : null;
}

/**
 * FORMATAGE — LA RÈGLE EST BINAIRE, DÉCIDÉE SUR LES CENTIMES.
 *
 * ⚠️ `minimumFractionDigits: 0` avec `maximumFractionDigits: 2` NE DONNE PAS ce qu'on veut :
 * JavaScript rogne les zéros de fin jusqu'au minimum, si bien que 4950 rendrait « 49,5 € » — un
 * prix qui ne s'écrit pas. Les deux bornes doivent donc être égales, et le choix se fait sur le
 * montant : multiple de 100 → zéro décimale ; sinon exactement deux.
 *
 * L'apparence ne change pas : « 50 € » avant ce lot, « 50 € » après. On change la source, pas
 * l'écran — sinon on ne saurait plus distinguer une régression visuelle d'un défaut de source.
 */
export function formatPrix({ amount, currency }: Tarif, locale = 'fr-FR'): string {
  const entier = amount % 100 === 0;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: entier ? 0 : 2,
    maximumFractionDigits: entier ? 0 : 2,
  }).format(amount / 100);
}

/** Libellé prêt à afficher, ou `null` — jamais une valeur de repli. */
export function libellePrix(etat: EtatTarifs, formule: CoachingFormuleId): string | null {
  const tarif = tarifDe(etat, formule);
  return tarif ? formatPrix(tarif) : null;
}

/** Le parcours de cette formule peut-il s'ouvrir ? Faux tant qu'aucun montant n'est connu. */
export function tarifPret(etat: EtatTarifs, formule: CoachingFormuleId): boolean {
  return tarifDe(etat, formule) !== null;
}
